import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle, MapPin, Calendar, Clock, ChevronLeft } from "lucide-react";
import { API } from "@/config/api";
import LaserBackground from "@/components/common/LaserBackground";
import CelebrationLasers from "@/components/common/CelebrationLasers";

const MesaPaymentSuccess = () => {
  const [searchParams] = useState(new URLSearchParams(window.location.search));
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [reservation, setReservation] = useState(null);
  const [error, setError] = useState('');
  const [event, setEvent] = useState(null);
  const [pollingCount, setPollingCount] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  const eventId = searchParams.get('event_id');
  const tipo = searchParams.get('tipo') || 'mesa';
  
  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        setError('Sesión de pago no encontrada');
        return;
      }
      
      try {
        const response = await axios.get(`${API}/mesas/payment-status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setReservation(response.data.reservation);
          
          // Fetch event info
          if (eventId) {
            const eventResponse = await axios.get(`${API}/events`);
            const foundEvent = eventResponse.data.find(e => e.id === eventId);
            if (foundEvent) setEvent(foundEvent);
          }
        } else if (response.data.status === 'expired' || response.data.status === 'cancelled') {
          setStatus('error');
          setError('El pago fue cancelado o expiró');
        } else if (pollingCount < 5) {
          // Keep polling
          setTimeout(() => setPollingCount(prev => prev + 1), 2000);
        } else {
          setStatus('error');
          setError('No se pudo verificar el pago. Por favor, contacta con soporte.');
        }
      } catch (e) {
        console.error('Error checking payment:', e);
        if (pollingCount < 5) {
          setTimeout(() => setPollingCount(prev => prev + 1), 2000);
        } else {
          setStatus('error');
          setError('Error al verificar el estado del pago');
        }
      }
    };
    
    checkPaymentStatus();
  }, [sessionId, eventId, pollingCount]);
  
  const tipoLabel = tipo === 'mesa' ? 'Mesa' : 'Reservado';
  
  return (
    <div className="hero-bg min-h-screen">
      <LaserBackground />
      <div className="main-container relative z-10">
        <div className="text-center mb-6 fade-in-up">
          <h1 className="font-logo text-3xl md:text-4xl text-white neon-glow">
            TAYTANTOS
          </h1>
        </div>
        
        {status === 'checking' && (
          <div className="form-section text-center fade-in-up">
            <div className="spinner mx-auto mb-4" style={{ width: 48, height: 48 }}></div>
            <h2 className="text-white text-xl mb-2">Verificando pago...</h2>
            <p className="text-[var(--text-muted)]">Por favor, espera mientras confirmamos tu pago.</p>
          </div>
        )}
        
        {status === 'error' && (
          <div className="form-section text-center fade-in-up">
            <XCircle size={48} className="text-[var(--error)] mx-auto mb-4" />
            <h2 className="text-[var(--error)] text-xl mb-2">Error en el pago</h2>
            <p className="text-[var(--text-muted)] mb-6">{error}</p>
            <button 
              onClick={() => navigate('/')}
              className="btn-primary"
            >
              Volver a Inicio
            </button>
          </div>
        )}
        
        {status === 'success' && reservation && (
          <div className="form-section success-section fade-in-up" data-testid="mesa-payment-success">
            <div className="w-full p-4 bg-gradient-to-r from-[var(--primary)]/20 via-[var(--primary)]/30 to-[var(--primary)]/20 border-y-2 border-[var(--primary)] mb-6">
              <p className="text-center text-[var(--primary)] font-bold text-xl uppercase tracking-widest">
                ¡Pago Completado!
              </p>
            </div>

            <CheckCircle size={48} className="text-[var(--success)] mx-auto mb-4" />
            <h3 className="text-white text-2xl font-bold text-center mb-2">
              ¡{tipoLabel} #{reservation.numero_mesa} Reservado!
            </h3>
            <p className="text-[var(--text-muted)] text-center mb-6">
              Te hemos enviado un email de confirmación con todos los detalles.
            </p>

            {event && (
              <div className="bg-[var(--surface)] p-4 border-l-4 border-[var(--accent-cyan)] mb-6">
                <h4 className="text-white font-bold mb-2">{event.nombre}</h4>
                <p className="text-[var(--text-muted)] text-sm">{event.fecha} - {event.hora}</p>
                <p className="text-[var(--text-muted)] text-sm">{event.sala}</p>
              </div>
            )}

            <div className="bg-[var(--surface)] p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">ID Reserva:</span>
                <span className="text-white font-bold">{reservation.id.slice(0, 8).toUpperCase()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">Tipo:</span>
                <span className="text-[var(--primary)] font-bold">{tipoLabel} #{reservation.numero_mesa}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">Nombre:</span>
                <span className="text-white">{reservation.nombre}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">Personas:</span>
                <span className="text-white">{reservation.num_personas}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">Pagado:</span>
                <span className="text-green-400 font-bold">{reservation.consumicion_minima?.toFixed(2) || '0.00'} €</span>
              </div>
            </div>

            <button 
              onClick={() => navigate('/')}
              className="btn-primary w-full mt-6"
            >
              Volver a Inicio
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MesaPaymentSuccess;
